import React, { useRef, useEffect, useState } from 'react';
import styled from 'styled-components';
import * as d3 from 'd3';

// Import the TRUE Restaurant model - adjust path if your models folder is elsewhere
import { Restaurant } from '../models/Restaurant';

// These interfaces can be local if only used by FortuneWheel,
// or you can move them to a models file (e.g., src/models/FortuneWheelModels.ts) and import them.
export interface WheelSegment {
  id: string | number; // Should match Restaurant['id'] type
  value: string;      // Typically Restaurant['name']
  color: string;
  textColor?: string;
}

export interface FortuneWheelConfig {
  segments: WheelSegment[];
  size: number;
  spinDuration: number;
  spinVelocity: number; // This property is defined but not used in the current spin logic
  spinReduction: number; // This property is defined but not used in the current spin logic
  outerBorderWidth: number;
  outerBorderColor: string;
  innerRadius: number;
  innerBorderWidth: number;
  innerBorderColor: string;
  radiusLineWidth: number;
  radiusLineColor: string;
  fontSize: number;
  textDistance: number; // This property is defined but not directly used if using centroid for text
}


interface FortuneWheelProps {
  restaurants: Restaurant[]; // Uses the imported Restaurant type
  onSpinEnd: (restaurant: Restaurant) => void; // Uses the imported Restaurant type
  isSpinning: boolean;
  setIsSpinning: (isSpinning: boolean) => void;
}

const defaultConfig: FortuneWheelConfig = {
  segments: [],
  size: 500, // Initial default, will be overridden by dynamic sizing
  spinDuration: 5000,
  spinVelocity: 800,
  spinReduction: 0.8,
  outerBorderWidth: 5,
  outerBorderColor: '#FFFFFF',
  innerRadius: 50, // Initial default
  innerBorderWidth: 4,
  innerBorderColor: '#FFFFFF',
  radiusLineWidth: 2,
  radiusLineColor: 'rgba(255, 255, 255, 0.5)',
  fontSize: 16, // Initial default
  textDistance: 40,
};

const FortuneWheel: React.FC<FortuneWheelProps> = ({
  restaurants,
  onSpinEnd,
  isSpinning,
  setIsSpinning
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [config, setConfig] = useState<FortuneWheelConfig>({ ...defaultConfig });
  const [rotation, setRotation] = useState(0);
  const [wheelSize, setWheelSize] = useState(300); // Default size, will be updated

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const windowHeight = window.innerHeight;
        
        // Calculate height available for wheel (accounting for modal padding and title)
        const availableHeight = windowHeight * 0.7;
        
        // Use the smallest dimension to ensure it fits on any device
        const newSize = Math.min(
          containerWidth > 50 ? containerWidth - 40 : 200, 
          availableHeight - 80,
          400
        );
        
        setWheelSize(Math.max(200, newSize)); // Minimum size 200px

        setConfig(prev => ({
          ...prev,
          size: Math.max(200, newSize),
          fontSize: Math.max(12, Math.min(16, newSize / 28)),
          innerRadius: Math.max(20, newSize / 10), // Scale inner radius
        }));
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    const colors = [
      '#FF5A5F', '#00A699', '#FC642D', '#4D5B9E', '#FF9A5B',
      '#00B5CC', '#FF385C', '#3886B9', '#F7B267', '#7FC8A9'
    ];

    const segments: WheelSegment[] = restaurants.map((restaurant, index) => ({
      id: restaurant.id, // Assuming restaurant.id exists and is string or number
      value: restaurant.name,
      color: colors[index % colors.length],
      textColor: '#FFFFFF',
    }));

    setConfig(prev => ({ ...prev, segments }));
  }, [restaurants]);

  useEffect(() => {
    if (!svgRef.current || config.segments.length === 0 || config.size <= 0) return;

    d3.select(svgRef.current).selectAll('*').remove();

    const width = config.size;
    const height = config.size;
    const radius = Math.min(width, height) / 2;

    const svg = d3.select(svgRef.current)
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2}) rotate(${rotation})`);

    const pie = d3.pie<WheelSegment>()
      .value(() => 1)
      .sort(null);

    const data = pie(config.segments);

    const arc = d3.arc<d3.PieArcDatum<WheelSegment>>()
      .innerRadius(config.innerRadius)
      .outerRadius(radius - config.outerBorderWidth);

    const segmentPaths = svg.selectAll('path.wheel-segment')
      .data(data)
      .enter()
      .append('path')
      .attr('class', 'wheel-segment')
      .attr('d', arc)
      .attr('fill', d => d.data.color)
      .attr('stroke', config.innerBorderColor)
      .attr('stroke-width', 1.5);

    segmentPaths.append('title')
      .text(d => d.data.value);

    const textFontSize = Math.max(9, Math.min(config.fontSize, (radius - config.innerRadius) / 7, 14));
    data.forEach((d) => {
      const name = d.data.value;
      const maxChars = Math.max(5, Math.floor((radius - config.innerRadius) / (textFontSize * 0.55))); // Adjusted divisor
      const displayName = name.length > maxChars ? name.substring(0, maxChars - 2) + '..' : name;

      const [cx, cy] = arc.centroid(d);
      let textAngleDegrees = ((d.startAngle + d.endAngle) / 2 * 180 / Math.PI) - 90;
      const midAngleForFlipCheck = (d.startAngle + d.endAngle) / 2 * 180 / Math.PI;

      if (midAngleForFlipCheck > 90 && midAngleForFlipCheck < 270) {
        textAngleDegrees += 180;
      }
      
      if (radius - config.innerRadius < 25 && config.segments.length > 12) return;

      svg.append('text')
        .attr('transform', `translate(${cx}, ${cy}) rotate(${textAngleDegrees})`)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'central')
        .attr('fill', d.data.textColor || '#FFFFFF')
        .attr('font-size', `${textFontSize}px`)
        .attr('font-weight', 'bold')
        .attr('pointer-events', 'none')
        .style('text-shadow', '0px 0px 3px rgba(0,0,0,0.7)')
        .text(displayName);
    });

    svg.append('circle')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', config.innerRadius)
      .attr('fill', 'rgba(255,255,255,0.8)')
      .attr('stroke', config.innerBorderColor)
      .attr('stroke-width', config.innerBorderWidth);

    if (config.segments.length > 1 && config.segments.length < 25) {
      svg.selectAll('line.radius-line')
        .data(data)
        .enter()
        .append('line')
        .attr('class', 'radius-line')
        .attr('x1', 0).attr('y1', 0)
        .attr('x2', (d) => (radius - config.outerBorderWidth) * Math.sin(d.startAngle))
        .attr('y2', (d) => -(radius - config.outerBorderWidth) * Math.cos(d.startAngle))
        .attr('stroke', config.radiusLineColor)
        .attr('stroke-width', config.radiusLineWidth / 2);
    }

    svg.append('circle')
      .attr('cx', 0).attr('cy', 0)
      .attr('r', radius - config.outerBorderWidth / 2)
      .attr('fill', 'none')
      .attr('stroke', config.outerBorderColor)
      .attr('stroke-width', config.outerBorderWidth);

  }, [config, rotation]);

  const spinWheel = () => {
    if (isSpinning || config.segments.length === 0) return;
    setIsSpinning(true);

    const spinAngle = 1800 + Math.floor(Math.random() * 1800);
    const targetRotation = rotation + spinAngle;
    const finalPosition = targetRotation % 360;
    const segmentCount = config.segments.length;
    const segmentSize = 360 / segmentCount;
    const rawWinnerIndex = Math.floor(((360 - finalPosition) % 360) / segmentSize);
    const winnerIndex = rawWinnerIndex;

    const winningSegment = config.segments[winnerIndex];
    if (!winningSegment) {
      console.error("Could not determine winning segment. Index:", winnerIndex);
      setIsSpinning(false);
      if (restaurants.length > 0) onSpinEnd(restaurants[0]); // Fallback
      return;
    }
    
    // Find the full restaurant object from the original restaurants prop
    const winningRestaurant = restaurants.find(r => r.id === winningSegment.id);
    if (!winningRestaurant) {
      console.error("Winning restaurant not found for ID:", winningSegment.id);
      setIsSpinning(false);
      if (restaurants.length > 0) onSpinEnd(restaurants[0]); // Fallback
      return;
    }

    const startTime = Date.now();
    const startAngle = rotation;
    const duration = config.spinDuration;
    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      if (elapsed < duration) {
        const progress = elapsed / duration;
        const easeOutCubic = 1 - Math.pow(1 - progress, 3);
        const currentRotation = startAngle + (spinAngle * easeOutCubic);
        setRotation(currentRotation);
        requestAnimationFrame(animate);
      } else {
        setRotation(targetRotation);
        setIsSpinning(false);
        onSpinEnd(winningRestaurant); // Pass the full Restaurant object
      }
    };
    requestAnimationFrame(animate);
  };

  return (
    <WheelContainer ref={containerRef}>
      <WheelOuter>
        {wheelSize > 0 && <WheelSVG ref={svgRef} width={wheelSize} height={wheelSize} />}
        <SpinButton
          onClick={spinWheel}
          disabled={isSpinning || config.segments.length === 0}
          // Dynamic sizing for the spin button based on innerRadius
          style={{
            width: `${Math.max(40, config.innerRadius * 1.8)}px`, 
            height: `${Math.max(40, config.innerRadius * 1.8)}px`, 
            fontSize: `${Math.max(10, config.innerRadius * 0.22)}px`,
            borderWidth: `${Math.max(2, config.innerRadius * 0.06)}px`
          }}
        >
          {isSpinning ? '...' : 'SPIN'}
        </SpinButton>
        <Pointer 
          // Dynamic sizing for the pointer
          style={{
            width: `${Math.max(15, config.innerRadius * 0.5)}px`, 
            height: `${Math.max(15, config.innerRadius * 0.5)}px`,
            top: `-${Math.max(2, config.innerRadius * 0.05)}px` // Adjust top based on size
          }}
        />
      </WheelOuter>
    </WheelContainer>
  );
};


const WheelContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 10px;
  margin: 0 auto;
  box-sizing: border-box;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  touch-action: none;
`;

const WheelOuter = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  max-width: 450px;
  min-width: 200px; /* Minimum practical size */
  aspect-ratio: 1 / 1;
  touch-action: none;
  
  @media (min-width: 1200px) {
    max-width: 500px;
  }
`;

const WheelSVG = styled.svg`
  display: block;
  touch-action: none;
  user-select: none;
  overflow: visible; 
`;

const SpinButton = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, var(--spinner-gradient-1, #FF385C), var(--spinner-gradient-2, #FF9A5B));
  color: white;
  /* border: 3px solid white; // now inline style */
  border-radius: 50%;
  font-weight: bold;
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  transition: opacity 0.2s ease-in-out, transform 0.1s ease-in-out, background-color 0.2s;
  touch-action: manipulation;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0; // remove padding if text is centered by flex
  
  &:hover:not(:disabled) {
    opacity: 0.85;
  }
  &:active:not(:disabled) {
    transform: translate(-50%, -50%) scale(0.95);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Pointer = styled.div`
  position: absolute;
  /* top: -5px; // now inline style */
  left: 50%;
  /* Adjusted transform to ensure the tip points from above the wheel boundary */
  transform: translateX(-50%) translateY(-100%) rotate(180deg); 
  clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
  background-color: var(--primary-color, #FF5A5F);
  z-index: 20; /* Ensure pointer is above everything, including SVG stroke */
  filter: drop-shadow(0px 1px 1px rgba(0,0,0,0.3));
`;

export default FortuneWheel;